import { RegularButton } from "@components/button"
import { BasicModal } from "@components/modal"
import { FlexBox } from "@components/wrapper"
import { CoursePlatformDetail } from "@interfaces/course"
import CheckoutServices from "@lib/api/services/checkout.services"
import clsx from "clsx"
import Image from "next/image"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"

interface ModalDigitalPlatformProp {
  show: boolean
  onClose: () => void
  platforms: CoursePlatformDetail[]
  courseId: string
}

interface CoursePlatform extends CoursePlatformDetail {
  isFullWidth: boolean
}

export const ModalDigitalPlatform = ({
  show,
  onClose,
  platforms,
  courseId,
}: ModalDigitalPlatformProp) => {
  const [digitalPlatforms, setDigitalPlatforms] = useState<CoursePlatform[]>([])
  const [voucherCode, setVoucherCode] = useState("")
  const [errorText, setErrorText] = useState("")

  const [isSubmitting, setIsSubmitting] = useState(false)

  const checkout = new CheckoutServices()
  const router = useRouter()

  useEffect(() => {
    if (!platforms || !platforms.length) {
      return
    }

    const rawPlatforms = [...platforms]
    const newPlatforms: CoursePlatform[] = []

    for (let i = 0; i < rawPlatforms.length; i++) {
      const platform = rawPlatforms[i]

      newPlatforms.push({
        ...platform,
        isFullWidth: rawPlatforms.length % 2 === 1 && i === rawPlatforms.length - 1,
      })
    }

    setDigitalPlatforms(newPlatforms)
  }, [platforms])

  const handleLink = (link: string) => () => {
    window.open(link, "_blank")
  }

  function handleInputVoucher(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value

    if (value.includes(" ")) {
      return
    }

    if (value.length > 18) {
      setErrorText("Maksimal 18 karakter")
    } else {
      setErrorText("")
    }

    setVoucherCode(value)
  }

  async function handleSubmitVoucher() {
    if (!voucherCode.length) {
      return setErrorText("Mohon isi kode voucher terlebih dahulu")
    }

    setIsSubmitting(true)

    try {
      const res = await checkout.redeemVoucher(courseId, voucherCode)

      if (res.isFailure) {
        const errorMessage = res.error.message
        setErrorText(errorMessage)
        setIsSubmitting(false)
      } else {
        const result = res.getValue()
        await router.push(`/invoice/${result.id}/external`)
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  return (
    <BasicModal show={show} onClose={onClose}>
      <div className={clsx("bg-primary-grey rounded-10 sm:p-8 sm:w-2/5 z-[9999]", "p-5 w-full")}>
        {Boolean(digitalPlatforms.length) && (
          <div>
            <div className="mb-4 responsive-font">
              Beli kelas melalui platform digital di bawah ini:
            </div>
            <FlexBox className="gap-4 flex-wrap">
              {digitalPlatforms.map((p, i) => (
                <button
                  key={i}
                  onClick={handleLink(p.platform_digital_url_course)}
                  className={clsx(
                    "border border-green rounded-10 py-3 flex items-center justify-center bg-white",
                    p.isFullWidth ? "w-full" : "w-[calc(50%-8px)]"
                  )}
                >
                  <Image
                    src={p.platform_digital_logo}
                    alt={p.platform_digital_name}
                    className="sm:h-5"
                    width={20}
                    height={20}
                  />
                </button>
              ))}
            </FlexBox>
            <div className="h-[1px] bg-green relative w-full sm:my-8 my-6 flex items-center justify-center">
              <div className="px-6 bg-primary-grey absolute responsive-font">Atau</div>
            </div>
          </div>
        )}

        <FlexBox direction="col" className="gap-4">
          <div className="responsive-font">Tukarkan kode voucher</div>
          <div>
            <input
              autoComplete="off"
              autoCapitalize="on"
              onChange={handleInputVoucher}
              placeholder="Kode Voucher"
              className={clsx(
                "py-3 sm:px-4 px-3 rounded-lg w-full border responsive-font",
                "focus:outline-none focus:shadow-outline",
                errorText ? "border-red" : "border-[#D1D0C6]"
              )}
            />
            {errorText && <small className="text-red">{errorText}</small>}
          </div>
          <RegularButton
            disabled={!voucherCode.length}
            isSubmitting={isSubmitting}
            className="w-full"
            customPadding="sm:py-3.5 py-2"
            onClick={handleSubmitVoucher}
          >
            Tukar Voucher
          </RegularButton>
        </FlexBox>
      </div>
    </BasicModal>
  )
}
